import { useNavigate } from "react-router-dom"
import { useState, useEffect, useMemo } from "react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { ArrowUpRight, ArrowDownRight, Wallet, LogOut, Menu, Check, Sun, Moon, ChevronDown, TrendingUp, TrendingDown, User } from "lucide-react"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { LabelList, Pie, PieChart } from "recharts"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { useAuth } from "@/lib/auth/auth-context"
import { useTheme } from "next-themes"
import { useTransactions } from "@/lib/context/transaction-context"
import { useAccountSelection } from "@/lib/context/account-selection-context"
import { toast } from "sonner"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetTrigger } from "@/components/ui/sheet"
import { formatTransactionDate } from "@/lib/services/transactions"
import { TransactionDetail } from "@/components/transaction-detail"
import type { Transaction } from "@/lib/types"
import { NotificationBell } from "@/components/notification-bell"
import { NavMenu } from "@/components/nav-menu"
import { convertTailwindToHex, getGradientClasses, darkenColor, isHexColor } from "@/lib/utils/color-helpers"
import { fetchUserBudgets } from "@/lib/services/budgets"
import type { Budget } from "@/lib/types"

interface DashboardProps {
  onAddExpense: () => void
  onViewAllTransactions: () => void
  onNavigate?: (view: string) => void
}

export function Dashboard({ onAddExpense, onViewAllTransactions, onNavigate }: DashboardProps) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const { transactions: allTransactions, totals, loading, refreshTransactions } = useTransactions()
  const { selectedAccountId, setSelectedAccountId, subAccounts, loading: loadingAccounts } = useAccountSelection()
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [showTransactionDetail, setShowTransactionDetail] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [chartPeriod, setChartPeriod] = useState<"today" | "week" | "month" | "last-month" | "year">("week")
  const [budgets, setBudgets] = useState<Budget[]>([])

  // Account totals for selected account
  const [accountTotals, setAccountTotals] = useState({ income: 0, expenses: 0, balance: 0 })

  // Get recent 4 transactions for dashboard
  // Filter by selected account and sort by date descending (latest/newest transactions at the top)
  const transactions = [...allTransactions]
    .filter(transaction => {
      // If "all" selected, show all transactions
      if (selectedAccountId === "all") return true
      // Otherwise, filter by selected account
      return transaction.subAccountId === selectedAccountId
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 4)

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success("Signed out successfully")
      navigate("/auth")
    } catch (error) {
      toast.error("Failed to sign out")
    }
  }

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  const getUserInitials = () => {
    if (!user) return "U"
    const firstName = user.user_metadata?.first_name
    const lastName = user.user_metadata?.last_name
    if (!firstName && !lastName) return "U"
    if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase()
    return (firstName || lastName || 'U')[0].toUpperCase()
  }

  const getUserName = () => {
    if (!user) return "User"
    const fullName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim()
    return fullName || user.email?.split("@")[0] || "User"
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return "Good Morning"
    if (hour < 18) return "Good Afternoon"
    return "Good Evening"
  }

  const handleTransactionClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setShowTransactionDetail(true)
  }

  const handleTransactionUpdate = async () => {
    // Reload transactions and totals after update
    await refreshTransactions()
  }

  const handleCloseDetail = () => {
    setShowTransactionDetail(false)
    setSelectedTransaction(null)
  }

  // Keep selectedTransaction in sync with latest data from context
  // This ensures the detail view shows real-time updates after editing
  useEffect(() => {
    if (selectedTransaction && showTransactionDetail) {
      const updated = allTransactions.find(t => t.id === selectedTransaction.id)
      // Only update if the transaction data has actually changed
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedTransaction)) {
        setSelectedTransaction(updated)
      }
    }
  }, [allTransactions, selectedTransaction?.id, showTransactionDetail])

  // Fetch budgets
  useEffect(() => {
    const loadBudgets = async () => {
      if (!user) return
      try {
        const userBudgets = await fetchUserBudgets(user.id)
        setBudgets(userBudgets)
      } catch (error) {
        console.error('Error loading budgets:', error)
      }
    }
    loadBudgets()
  }, [user])

  // Calculate totals for selected account
  useEffect(() => {
    const calculateAccountTotals = () => {
      // Performance optimization: use context totals for "all" view
      if (selectedAccountId === "all") {
        console.log('📊 [DASHBOARD] Using context totals for all accounts:', totals)
        setAccountTotals(totals)
        return
      }

      // Only calculate when specific account is selected
      const filteredTransactions = allTransactions.filter(
        transaction => transaction.subAccountId === selectedAccountId
      )

      console.log('📊 [DASHBOARD] Calculating totals for account:', {
        accountId: selectedAccountId,
        transactionCount: filteredTransactions.length,
        totalTransactions: allTransactions.length
      })

      // Calculate totals for filtered transactions
      const income = filteredTransactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + t.amount, 0)

      const expenses = filteredTransactions
        .filter(t => t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0)

      const balance = income - expenses

      console.log('📊 [DASHBOARD] Account totals calculated:', { income, expenses, balance })
      setAccountTotals({ income, expenses, balance })
    }

    calculateAccountTotals()
  }, [selectedAccountId, allTransactions, totals])

  const handleAccountChange = (accountId: string) => {
    console.log('🔄 [DASHBOARD] Account changed:', {
      from: selectedAccountId,
      to: accountId,
      timestamp: new Date().toISOString()
    })
    setSelectedAccountId(accountId)
  }

  const getSelectedAccountName = () => {
    if (selectedAccountId === "all") return "All Accounts"
    const account = subAccounts.find(acc => acc.id === selectedAccountId)
    return account ? account.name : "All Accounts"
  }

  const getCardGradient = () => {
    // Default gradient for "All Accounts"
    if (selectedAccountId === "all") {
      return { className: "from-slate-900 via-slate-800 to-slate-900", style: undefined }
    }

    // Get selected account's color
    const account = subAccounts.find(acc => acc.id === selectedAccountId)
    if (!account?.color) {
      return { className: "from-slate-900 via-slate-800 to-slate-900", style: undefined }
    }

    // Check if color is hex or Tailwind class
    const hexColor = convertTailwindToHex(account.color)

    // If it's a hex color (new format), use inline style
    if (isHexColor(hexColor)) {
      const darkColor = darkenColor(hexColor, 30)
      const mediumColor = darkenColor(hexColor, 15)

      return {
        className: "",
        style: {
          background: `linear-gradient(135deg, ${darkColor} 0%, ${mediumColor} 50%, ${darkColor} 100%)`
        }
      }
    }

    // Otherwise use Tailwind gradient classes (backward compatibility)
    const gradientClasses = getGradientClasses(account.color)
    return { className: gradientClasses || "from-slate-900 via-slate-800 to-slate-900", style: undefined }
  }

  // Calculate spending data for chart
  const spendingChartData = useMemo(() => {
    const now = new Date()
    const expenses = allTransactions.filter(t => {
      if (selectedAccountId !== "all" && t.subAccountId !== selectedAccountId) return false
      return t.amount < 0 // Only expenses
    })

    // Get total monthly budget for the selected account
    const accountBudgets = budgets.filter(b => {
      if (selectedAccountId === "all") return true
      return b.subAccountId === selectedAccountId
    })
    const totalMonthlyBudget = accountBudgets.reduce((sum, b) => {
      if (b.period === 'monthly') return sum + b.amount
      if (b.period === 'weekly') return sum + (b.amount * 4.33) // Convert to monthly
      if (b.period === 'yearly') return sum + (b.amount / 12) // Convert to monthly
      return sum
    }, 0)

    let data: { date: string; spending: number; budget: number }[] = []

    if (chartPeriod === "today") {
      // Show hourly data for today
      const dailyBudget = totalMonthlyBudget / 30 // Approximate daily budget
      const hourlyBudget = dailyBudget / 24
      for (let i = 0; i < 24; i++) {
        data.push({ date: `${i}:00`, spending: 0, budget: hourlyBudget })
      }
      expenses.forEach(expense => {
        const expenseDate = new Date(expense.date)
        if (expenseDate.toDateString() === now.toDateString()) {
          const hour = expenseDate.getHours()
          data[hour].spending += Math.abs(expense.amount)
        }
      })
    } else if (chartPeriod === "week") {
      // Show last 7 days
      const dailyBudget = totalMonthlyBudget / 30 // Approximate daily budget
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now)
        date.setDate(date.getDate() - i)
        data.push({ date: date.toLocaleDateString('en-US', { weekday: 'short' }), spending: 0, budget: dailyBudget })
      }
      expenses.forEach(expense => {
        const expenseDate = new Date(expense.date)
        const daysDiff = Math.floor((now.getTime() - expenseDate.getTime()) / (1000 * 60 * 60 * 24))
        if (daysDiff >= 0 && daysDiff < 7) {
          data[6 - daysDiff].spending += Math.abs(expense.amount)
        }
      })
    } else if (chartPeriod === "month") {
      // Show current month day by day
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
      const dailyBudget = totalMonthlyBudget / daysInMonth
      for (let i = 1; i <= daysInMonth; i++) {
        data.push({ date: `${i}`, spending: 0, budget: dailyBudget })
      }
      expenses.forEach(expense => {
        const expenseDate = new Date(expense.date)
        if (expenseDate.getMonth() === now.getMonth() && expenseDate.getFullYear() === now.getFullYear()) {
          const day = expenseDate.getDate()
          data[day - 1].spending += Math.abs(expense.amount)
        }
      })
    } else if (chartPeriod === "last-month") {
      // Show last month day by day
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const daysInLastMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate()
      const dailyBudget = totalMonthlyBudget / daysInLastMonth
      for (let i = 1; i <= daysInLastMonth; i++) {
        data.push({ date: `${i}`, spending: 0, budget: dailyBudget })
      }
      expenses.forEach(expense => {
        const expenseDate = new Date(expense.date)
        if (expenseDate.getMonth() === lastMonth.getMonth() && expenseDate.getFullYear() === lastMonth.getFullYear()) {
          const day = expenseDate.getDate()
          data[day - 1].spending += Math.abs(expense.amount)
        }
      })
    } else if (chartPeriod === "year") {
      // Show current year month by month
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      for (let i = 0; i < 12; i++) {
        data.push({ date: months[i], spending: 0, budget: totalMonthlyBudget })
      }
      expenses.forEach(expense => {
        const expenseDate = new Date(expense.date)
        if (expenseDate.getFullYear() === now.getFullYear()) {
          const month = expenseDate.getMonth()
          data[month].spending += Math.abs(expense.amount)
        }
      })
    }

    return data
  }, [allTransactions, selectedAccountId, chartPeriod, budgets])

  // Calculate max Y value for chart
  const maxSpending = useMemo(() => {
    const maxSpent = Math.max(...spendingChartData.map(d => d.spending))
    const maxBudget = Math.max(...spendingChartData.map(d => d.budget))
    const max = Math.max(maxSpent, maxBudget)
    // Round up to nearest 100
    return Math.ceil(max / 100) * 100 || 500
  }, [spendingChartData])

  return (
    <div className="space-y-6 p-4">
      <div className="max-w-md mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <NavMenu onNavigate={(view) => {
              setMenuOpen(false)
              onNavigate?.(view)
            }} />
          </Sheet>
          <div>
            <p className="text-sm text-muted-foreground">{getGreeting()}</p>
            <h1 className="text-xl font-bold">{getUserName()}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full p-0">
                <Avatar className="h-10 w-10 border-2 border-primary/20">
                  <AvatarImage src={user?.user_metadata?.avatar_url} alt="Profile" />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onNavigate?.("profile")} className="cursor-pointer font-semibold">
                <User className="mr-2 h-4 w-4" />
                My Account
              </DropdownMenuItem>
              <DropdownMenuSeparator />

              {/* Theme Toggle */}
              <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="cursor-pointer">
                <div className="flex items-center justify-between w-full gap-3">
                  <div className="flex items-center gap-2">
                    {theme === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                    <span>Dark Mode</span>
                  </div>
                  <Switch
                    checked={theme === "dark"}
                    onCheckedChange={toggleTheme}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Balance Card - Credit Card Design */}
      <Card
        className={`relative overflow-hidden bg-gradient-to-br ${getCardGradient().className} p-5 text-white shadow-2xl shadow-black/40 rounded-2xl aspect-[2/1] border-slate-700/50`}
        style={getCardGradient().style}
      >
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl" />
        </div>

        <div className="relative h-full flex flex-col justify-between">
          {/* Top row: Chip + Account Switcher */}
          <div className="flex items-start justify-between">
            {/* EMV Chip Icon + Account Name */}
            <div className="flex flex-col gap-2">
              <div className="h-10 w-12 rounded-md bg-gradient-to-br from-amber-200 via-amber-300 to-amber-400 shadow-lg relative overflow-hidden">
                <div className="absolute inset-0.5 bg-gradient-to-br from-amber-300 to-amber-500 rounded-sm"></div>
                <div className="absolute inset-1 grid grid-cols-3 grid-rows-3 gap-0.5 p-1">
                  {[...Array(9)].map((_, i) => (
                    <div key={i} className="bg-amber-600/30 rounded-[1px]" />
                  ))}
                </div>
              </div>
              <p className="text-[10px] uppercase tracking-wide text-white/60 font-medium max-w-[100px] truncate">
                {getSelectedAccountName()}
              </p>
            </div>

            {/* Account Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5">
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                    <g id="SVGRepo_iconCarrier">
                      <path d="M18 10L21 7M21 7L18 4M21 7H7M6 14L3 17M3 17L6 20M3 17H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                    </g>
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glassmorphism border-white/20">
                <DropdownMenuItem
                  onClick={() => handleAccountChange("all")}
                  className="cursor-pointer"
                >
                  <span className="flex items-center justify-between w-full">
                    <span>All Accounts</span>
                    {selectedAccountId === "all" && <Check className="h-4 w-4 ml-2" />}
                  </span>
                </DropdownMenuItem>
                {subAccounts.map((account) => (
                  <DropdownMenuItem
                    key={account.id}
                    onClick={() => handleAccountChange(account.id)}
                    className="cursor-pointer"
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>{account.icon} {account.name}</span>
                      {selectedAccountId === account.id && <Check className="h-4 w-4 ml-2" />}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Middle section: Balance + Cardholder */}
          <div className="flex items-end justify-between mt-auto mb-3">
            {/* Balance - Left Side */}
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-widest text-white/60 font-medium">Total Balance</p>
              <h2 className="text-4xl font-bold tracking-tight">
                {loading || loadingAccounts ? '...' : `$${accountTotals.balance.toFixed(2)}`}
              </h2>
            </div>

            {/* Cardholder - Right Side */}
            <div className="text-right">
              <p className="text-xs text-white/50 uppercase tracking-wide mb-0.5">Cardholder</p>
              <p className="text-sm font-medium tracking-wide">{getUserName()}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Income and Expenses Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 bg-income/5 border-income/20">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-income/10 p-1.5">
                <ArrowDownRight className="h-4 w-4 text-income" />
              </div>
              <p className="text-sm text-muted-foreground">Income</p>
            </div>
            <p className="text-xl font-bold text-income">
              {loading || loadingAccounts ? '...' : `$${accountTotals.income.toFixed(2)}`}
            </p>
          </div>
        </Card>
        <Card className="p-4 bg-expense/5 border-expense/20">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="rounded-full bg-expense/10 p-1.5">
                <ArrowUpRight className="h-4 w-4 text-expense" />
              </div>
              <p className="text-sm text-muted-foreground">Expenses</p>
            </div>
            <p className="text-xl font-bold text-expense">
              {loading || loadingAccounts ? '...' : `$${accountTotals.expenses.toFixed(2)}`}
            </p>
          </div>
        </Card>
      </div>


      {/* Financial Overview */}
      <Card className="flex flex-col">
        <CardHeader className="items-center pb-0">
          <CardTitle>Financial Overview</CardTitle>
          <CardDescription>
            {selectedAccountId === "all"
              ? "All Accounts"
              : subAccounts.find(a => a.id === selectedAccountId)?.name || "Selected Account"
            } - Balance: ${accountTotals.balance.toFixed(2)}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 pb-0">
          {loading || loadingAccounts ? (
            <div className="flex items-center justify-center h-[250px]">
              <p className="text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : accountTotals.income === 0 && accountTotals.expenses === 0 ? (
            <div className="flex items-center justify-center h-[250px]">
              <p className="text-sm text-muted-foreground">No financial data yet</p>
            </div>
          ) : (
            <ChartContainer
              config={{
                amount: {
                  label: "Amount",
                },
                income: {
                  label: "Income",
                  color: "var(--income)",
                },
                expenses: {
                  label: "Expenses",
                  color: "var(--expense)",
                },
              } satisfies ChartConfig}
              className="mx-auto aspect-square max-h-[250px]"
            >
              <PieChart>
                <ChartTooltip
                  content={<ChartTooltipContent nameKey="amount" hideLabel />}
                />
                <Pie
                  data={[
                    { category: "income", amount: accountTotals.income, fill: "var(--income)" },
                    { category: "expenses", amount: accountTotals.expenses, fill: "var(--expense)" },
                  ]}
                  dataKey="amount"
                  nameKey="category"
                >
                  <LabelList
                    dataKey="category"
                    className="fill-background"
                    stroke="none"
                    fontSize={12}
                    formatter={(value: string) =>
                      value === "income" ? "Income" : "Expenses"
                    }
                  />
                </Pie>
              </PieChart>
            </ChartContainer>
          )}
        </CardContent>
        <CardFooter className="flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 leading-none font-medium">
            {accountTotals.balance >= 0 ? (
              <>
                Positive balance <TrendingUp className="h-4 w-4 text-income" />
              </>
            ) : (
              <>
                Negative balance <TrendingDown className="h-4 w-4 text-expense" />
              </>
            )}
          </div>
          <div className="flex items-center justify-center gap-4 text-muted-foreground leading-none">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-income" />
              <span>+${accountTotals.income.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-expense" />
              <span>-${accountTotals.expenses.toFixed(2)}</span>
            </div>
          </div>
        </CardFooter>
      </Card>

      {/* Recent Transactions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">Recent Transactions</h3>
          <Button variant="ghost" size="sm" className="text-primary" onClick={onViewAllTransactions}>
            See All
          </Button>
        </div>

        <div className="space-y-2">
          {loading ? (
            <Card className="p-4">
              <p className="text-center text-muted-foreground">Loading transactions...</p>
            </Card>
          ) : transactions.length === 0 ? (
            <Card className="p-4">
              <p className="text-center text-muted-foreground">No transactions yet</p>
            </Card>
          ) : (
            transactions.map((transaction) => (
              <Card
                key={transaction.id}
                className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleTransactionClick(transaction)}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-12 w-12 rounded-xl ${transaction.color} flex items-center justify-center text-xl`}>
                    {transaction.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{transaction.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {transaction.category} • {formatTransactionDate(transaction.date)}
                    </p>
                  </div>
                  <div className={`text-base font-semibold ${transaction.amount > 0 ? "text-income" : "text-expense"}`}>
                    {transaction.amount > 0 ? "+" : "-"}${Math.abs(transaction.amount).toFixed(2)}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Transaction Detail Dialog */}
      <TransactionDetail
        transaction={selectedTransaction}
        open={showTransactionDetail}
        onClose={handleCloseDetail}
        onUpdate={handleTransactionUpdate}
      />
      </div>
    </div>
  )
}
